import React from 'react';
import '../styles/NightSky.css';

/*
 * The dark theme's backdrop: a starfield with things passing through it.
 *
 * Everything here is decoration. The layer is aria-hidden and pointer-events
 * none, so it can never absorb a click or reach a screen reader.
 *
 * The field is generated once at module load from a seeded generator, so it is
 * identical on every render and every reload -- a sky that reshuffled itself on
 * each navigation would read as noise.
 */

const VB_W = 1600;
const VB_H = 900;

/** Small deterministic PRNG, so the sky is stable across renders. */
const seeded = (seed) => {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
};

/*
 * Stellar colour, roughly by temperature: mostly white, with blue-white giants
 * and a scattering of amber and rose. A monochrome field reads as dust.
 */
const TINTS = [
  '#dce6ff', '#dce6ff', // blue-white
  '#f4f6ff', '#f4f6ff', '#f4f6ff', '#f4f6ff', // white, the bulk
  '#fff1dc', '#ffe4bd', // warm
  '#ffd6d0', // rose
];

/*
 * Minimum separation between stars. Pure random placement clumps -- pairs land
 * touching and the eye reads them as one smudged blob rather than two points.
 * The largest flare reaches ~7 units, so 34 keeps even neighbouring spiked
 * stars visibly apart.
 */
const MIN_GAP = 34;

const build = () => {
  const random = seeded(20260913);
  const stars = [];
  const tooClose = (x, y) =>
    stars.some((s) => (s.cx - x) ** 2 + (s.cy - y) ** 2 < MIN_GAP * MIN_GAP);

  for (let i = 0; i < 190; i += 1) {
    const bright = random();
    // The brightest few get shapes; everything else stays a point of light.
    let kind = 'dot';
    if (bright > 0.955) kind = 'spike';
    else if (bright > 0.87) kind = 'spark';
    // Rejection sampling: try a few spots, and skip this star if the sky is
    // locally full rather than forcing it on top of a neighbour.
    let cx = 0;
    let cy = 0;
    let placed = false;
    for (let attempt = 0; attempt < 30; attempt += 1) {
      cx = random() * VB_W;
      cy = random() * VB_H;
      if (!tooClose(cx, cy)) {
        placed = true;
        break;
      }
    }
    if (!placed) continue;

    stars.push({
      cx: +cx.toFixed(1),
      cy: +cy.toFixed(1),
      r: +(0.45 + bright * bright * 1.65).toFixed(2),
      o: +(0.2 + bright * 0.62).toFixed(2),
      tint: TINTS[Math.floor(random() * TINTS.length)],
      kind,
      // Only the brighter ones twinkle; a whole sky of blinking is noise.
      twinkle: bright > 0.7,
      delay: +(random() * 9).toFixed(1),
      dur: +(3.5 + random() * 4).toFixed(1),
    });
  }
  return stars;
};

/** A soft four-pointed sparkle -- concave sides, so it reads as light. */
const sparkPath = (r) =>
  `M 0 ${-r} Q 0 0 ${r} 0 Q 0 0 0 ${r} Q 0 0 ${-r} 0 Q 0 0 0 ${-r} Z`;

const STARS = build();

/* Streaks: angle, start point, travel, and how often one comes round. */
const SHOOTING = [
  { x: 180, y: 90, angle: 24, dist: 620, dur: 17, delay: 2 },
  { x: 1180, y: 60, angle: 33, dist: 520, dur: 23, delay: 11 },
  { x: 640, y: 330, angle: 18, dist: 460, dur: 31, delay: 19 },
];

/*
 * Two builds of spacecraft, each on a long transit and tumbling slowly on its
 * own axis -- a craft that held perfect attitude would read as a sticker.
 */
const comsat = (
  <>
    <rect x={-8} y={-5.5} width={16} height={11} rx={1.8} className="ns-bus" />
    <rect x={-30} y={-3.6} width={19} height={7.2} rx={1} className="ns-panel" />
    <rect x={11} y={-3.6} width={19} height={7.2} rx={1} className="ns-panel" />
    <line x1={-11} y1={0} x2={-8} y2={0} className="ns-strut" />
    <line x1={8} y1={0} x2={11} y2={0} className="ns-strut" />
    <path d="M -5.5 7 A 6.5 6.5 0 0 0 5.5 7 Z" className="ns-bus" />
    <line x1={0} y1={5.5} x2={0} y2={10} className="ns-strut" />
    <circle cx={0} cy={-7.6} r={1.5} className="ns-beacon" />
  </>
);

const probe = (
  <>
    {/* Hexagonal bus with a high-gain dish and an instrument boom. */}
    <path d="M -6 -7 L 6 -7 L 10 0 L 6 7 L -6 7 L -10 0 Z" className="ns-bus" />
    <ellipse cx={0} cy={-13} rx={11} ry={4} className="ns-dish" />
    <line x1={0} y1={-7} x2={0} y2={-11} className="ns-strut" />
    <line x1={10} y1={0} x2={27} y2={-6} className="ns-strut" />
    <circle cx={28} cy={-6.5} r={2} className="ns-bus" />
    <rect x={-30} y={-2.6} width={20} height={5.2} rx={1} className="ns-panel" />
    <circle cx={0} cy={9} r={1.4} className="ns-beacon" />
  </>
);

const CRAFT = [
  { y: 62, dur: 68, delay: 4, scale: 1.4, drift: -26, spin: 46, art: comsat },
  { y: 148, dur: 96, delay: 38, scale: 1.0, drift: 20, spin: 63, art: probe, reverse: true },
];

const NightSky = () => (
  <div className="night-sky" aria-hidden="true">
    <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="xMidYMid slice" focusable="false">
      <defs>
        <linearGradient id="ns-streak" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#fff" stopOpacity="0" />
          <stop offset="100%" stopColor="#fff" stopOpacity=".9" />
        </linearGradient>
      </defs>

      {STARS.map((star, index) => {
        const style = {
          '--o': star.o,
          '--delay': `${star.delay}s`,
          '--dur': `${star.dur}s`,
          '--tint': star.tint,
        };
        const cls = `ns-star${star.twinkle ? ' is-twinkling' : ''}`;

        if (star.kind === 'dot') {
          return <circle key={index} className={cls} cx={star.cx} cy={star.cy} r={star.r} style={style} />;
        }

        return (
          <g key={index} className={cls} transform={`translate(${star.cx} ${star.cy})`} style={style}>
            {star.kind === 'spike' && (
              <>
                {/* Diffraction spikes: only the very brightest earn them. */}
                <line className="ns-spike" x1={-star.r * 3.3} y1={0} x2={star.r * 3.3} y2={0} />
                <line className="ns-spike" x1={0} y1={-star.r * 3.3} x2={0} y2={star.r * 3.3} />
              </>
            )}
            <path className="ns-spark" d={sparkPath(star.r * 1.85)} />
            <circle className="ns-core" cx={0} cy={0} r={star.r * 0.62} />
          </g>
        );
      })}

      {SHOOTING.map((shot, index) => (
        <g key={`shot-${index}`} transform={`translate(${shot.x} ${shot.y})`}>
          <g
            className="ns-shot"
            style={{
              '--angle': `${shot.angle}deg`,
              '--dist': `${shot.dist}px`,
              '--dur': `${shot.dur}s`,
              '--delay': `${shot.delay}s`,
            }}
          >
            <line x1={-64} y1={0} x2={0} y2={0} stroke="url(#ns-streak)" strokeWidth="1.6" strokeLinecap="round" />
            <circle cx={0} cy={0} r={1.7} fill="#fff" />
          </g>
        </g>
      ))}

      {CRAFT.map((craft, index) => (
        <g
          key={`craft-${index}`}
          className="ns-craft"
          style={{
            '--y': `${craft.y}px`,
            '--drift': `${craft.drift}px`,
            '--dur': `${craft.dur}s`,
            '--delay': `${craft.delay}s`,
            '--scale': craft.scale,
          }}
        >
          {/* Attitude is animated separately, so tumble and transit compose. */}
          <g
            className={`ns-craft-spin${craft.reverse ? ' is-reverse' : ''}`}
            style={{ '--spin': `${craft.spin}s` }}
          >
            {craft.art}
          </g>
        </g>
      ))}
    </svg>
  </div>
);

export default NightSky;
